import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem, Card, CardBody, CardHeader, Table, Input, Modal, ModalHeader, ModalBody, ModalFooter, Badge, Button } from "reactstrap";
import cogoToast from "cogo-toast";
import { genericPost, getProjects, updateComponentProjectApi } from "../../services/Api";
import { ENDPOINTS, SUCCESS_RESULT, DEFAULT_RESULT } from "../../Constants";
import { FaCheckCircle, FaChild, FaExclamationCircle, FaInfoCircle, FaMapMarkerAlt, FaPhone, FaExclamationTriangle, FaChartBar, FaFileDownload, FaExchangeAlt } from "react-icons/fa";
import ability from "../config/defineAbility";
import { PERMS } from "../config/perms";
import LocationPickerModal from "../../components/LocationPickerModal";
import CriteriaSummaryModal from "../../components/CriteriaSummaryModal";

const PAGE_SIZE = 500;
const LARGE_LIST_THRESHOLD = 2000;

const CAMP_CARD_GRADIENTS = [
  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
  "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
  "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
  "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
  "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
  "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
  "linear-gradient(135deg, #ec4899 0%, #db2777 100%)",
  "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
  "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
];

const CAMP_CARD_STAGGER_MS = 100;

const ALL_CAMPS_GRADIENT = "linear-gradient(135deg, #1e293b 0%, #475569 100%)";

const getCampName = (r) => {
  const d = r?.beneficiary_details || {};
  return d?.camp_name || r?.camp_name || "Unknown";
};

const normalizeCampStats = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  return raw
    .map((item) => ({
      camp_name: item?.camp_name || item?.name || item?.camp || "Unknown",
      count: Number(item?.count ?? item?.total ?? item?.beneficiary_count ?? item?.total_registered ?? 0),
    }))
    .filter((item) => item.camp_name)
    .sort((a, b) => b.count - a.count);
};

const ActivityBeneficiaries = () => {
  const { component_id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const projectId = location?.state?.project_id || null;
  const projectName = location?.state?.project_name || "";
  const activityName = location?.state?.component_name || "Activity Beneficiaries";
  const totalRegisteredHint = Number(location?.state?.total_registered) || 0;

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedMealRow, setSelectedMealRow] = useState(null);
  const [selectedSimilarRow, setSelectedSimilarRow] = useState(null);
  const [isVerifyMode, setIsVerifyMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyComment, setVerifyComment] = useState("");
  const [verifyAction, setVerifyAction] = useState("");
  const [isSubmittingVerify, setIsSubmittingVerify] = useState(false);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
  const [isFilterLocationPickerOpen, setIsFilterLocationPickerOpen] = useState(false);
  const [appliedLocationFilter, setAppliedLocationFilter] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedDateFilter, setAppliedDateFilter] = useState(null);
  const [showUpdateLocationModal, setShowUpdateLocationModal] = useState(false);
  const [updateLocationStep, setUpdateLocationStep] = useState(1);
  const [selectedLocationBeneficiaryIds, setSelectedLocationBeneficiaryIds] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isSubmittingLocationUpdate, setIsSubmittingLocationUpdate] = useState(false);
  const [locationModalSearch, setLocationModalSearch] = useState("");
  const [locationModalSortField, setLocationModalSortField] = useState("name");
  const [locationModalSortDirection, setLocationModalSortDirection] = useState("asc");
  const [selectionCriteriaTemplate, setSelectionCriteriaTemplate] = useState(null);
  const [criteriaSummaryRow, setCriteriaSummaryRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [useServerPagination, setUseServerPagination] = useState(false);
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    last_page: 1,
    per_page: PAGE_SIZE,
  });
  const [campStatsFromApi, setCampStatsFromApi] = useState(null);
  const [selectedCampFilter, setSelectedCampFilter] = useState(null);
  const [hasLoadedBeneficiaries, setHasLoadedBeneficiaries] = useState(
    () => totalRegisteredHint <= LARGE_LIST_THRESHOLD
  );
  const [showDistributionSummaryModal, setShowDistributionSummaryModal] = useState(false);
  const [distributionSummaryLoading, setDistributionSummaryLoading] = useState(false);
  const [distributionSummaryCycles, setDistributionSummaryCycles] = useState([]);
  const [showMasterListModal, setShowMasterListModal] = useState(false);
  const [masterListCycles, setMasterListCycles] = useState([]);
  const [masterListCyclesLoading, setMasterListCyclesLoading] = useState(false);
  const [isGeneratingMasterList, setIsGeneratingMasterList] = useState(false);
  const [masterListForm, setMasterListForm] = useState({
    cycle_id: "",
    masterlist_type: "",
    template_status: "new",
    start_date: "",
    end_date: "",
  });
  const canGenerateMasterList = ability.can(PERMS.READ, PERMS.GENERATE_MASTER_LIST);
  const canViewFailedCriterion = ability.can(PERMS.READ, PERMS.VIEW_PROJECT_WORKFLOW);
  const canVerifyMeal = ability.can(PERMS.UPDATE, PERMS.CAN_VERIFY_BENEFICIARIES_AS_MEAL_OFFICER);
  const canUpdateBeneficiaryLocation = ability.can(
    PERMS.UPDATE,
    PERMS.CAN_UPDATE_BENEFICIARY_LOCATION_DETAILS
  );
  const canUpdateComponentProject = ability.can(PERMS.UPDATE, PERMS.CAN_UPDATE_COMPONENT_PROJECT);

  // --- Move component to another project ---
  const [showMoveComponentModal, setShowMoveComponentModal] = useState(false);
  const [allProjects, setAllProjects] = useState([]);
  const [allProjectsLoading, setAllProjectsLoading] = useState(false);
  const [selectedNewProjectId, setSelectedNewProjectId] = useState("");
  const [isMovingComponent, setIsMovingComponent] = useState(false);

  const loadBeneficiaries = (options = {}) => {
    if (!component_id) return;
    const locationFilter =
      options.locationFilter !== undefined ? options.locationFilter : appliedLocationFilter;
    const dateFilter = options.dateFilter !== undefined ? options.dateFilter : appliedDateFilter;
    const page = options.page !== undefined ? options.page : currentPage;

    setIsLoading(true);
    const payload = {
      component_id,
      project_id: projectId,
      page,
      per_page: PAGE_SIZE,
    };
    if (locationFilter?.country) {
      payload.location = locationFilter;
    }
    if (dateFilter?.start_date && dateFilter?.end_date) {
      payload.start_date = dateFilter.start_date;
      payload.end_date = dateFilter.end_date;
    }
    genericPost(ENDPOINTS.LIST_ACTIVE_BENEFICIARIES_DETAILS, {
      ...payload,
    })
      .then((res) => {
        setIsLoading(false);
        if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
          const responseData = res?.data?.data;
          const list = Array.isArray(responseData)
            ? responseData
            : Array.isArray(responseData?.beneficiaries)
            ? responseData.beneficiaries
            : [];
          const apiPagination = responseData?.pagination || res?.data?.pagination || null;
          const hasServerPagination =
            apiPagination &&
            (apiPagination.last_page > 1 ||
              apiPagination.total > list.length ||
              apiPagination.current_page > 1);

          setRows(list);
          setCurrentPage(apiPagination?.current_page || page);

          const apiCampStats = normalizeCampStats(
            responseData?.camp_summary ||
              responseData?.camp_counts ||
              responseData?.camps ||
              res?.data?.camp_summary
          );
          setCampStatsFromApi(apiCampStats);

          if (hasServerPagination) {
            setUseServerPagination(true);
            setPaginationMeta({
              total: apiPagination.total ?? list.length,
              last_page: apiPagination.last_page || 1,
              per_page: apiPagination.per_page || PAGE_SIZE,
              has_more_pages: !!apiPagination.has_more_pages,
            });
          } else {
            setUseServerPagination(false);
            const total = list.length;
            setPaginationMeta({
              total,
              last_page: Math.max(1, Math.ceil(total / PAGE_SIZE)),
              per_page: PAGE_SIZE,
            });
          }
        } else {
          cogoToast.error(res?.data?.message || "Failed to load active beneficiaries", { hideAfter: 4 });
          setRows([]);
          setCampStatsFromApi(null);
          setPaginationMeta({ total: 0, last_page: 1, per_page: PAGE_SIZE });
        }
      })
      .catch((e) => {
        setIsLoading(false);
        console.error("Active beneficiaries load error", e);
        cogoToast.error("Failed to load active beneficiaries", { hideAfter: 4 });
        setRows([]);
        setCampStatsFromApi(null);
        setPaginationMeta({ total: 0, last_page: 1, per_page: PAGE_SIZE });
      });
  };

  const loadSelectionCriteriaTemplate = () => {
    if (!component_id || !projectId) return;

    genericPost(ENDPOINTS.COMPONENT_SELECTION_CRITERIA_TEMPLATE, {
      project_id: projectId,
      component_id,
    })
      .then((res) => {
        const apiStatus = res?.data?.status;
        if (
          res?.status === 200 &&
          (apiStatus === SUCCESS_RESULT || apiStatus === DEFAULT_RESULT)
        ) {
          const template = res?.data?.data?.selection_criteria_template;
          if (template) {
            setSelectionCriteriaTemplate(template);
          }
        } else {
          console.warn(
            "Selection criteria template:",
            res?.data?.message || "Failed to load"
          );
        }
      })
      .catch((e) => {
        console.error("Selection criteria template load error", e);
      });
  };

  useEffect(() => {
    const deferLoad = totalRegisteredHint > LARGE_LIST_THRESHOLD;
    setCurrentPage(1);
    setSelectedCampFilter(null);
    setAppliedLocationFilter(null);
    setAppliedDateFilter(null);
    setStartDate("");
    setEndDate("");
    setHasLoadedBeneficiaries(!deferLoad);
    if (deferLoad) {
      setRows([]);
      setCampStatsFromApi(null);
      setPaginationMeta({ total: 0, last_page: 1, per_page: PAGE_SIZE });
    } else {
      loadBeneficiaries({ page: 1, locationFilter: null, dateFilter: null });
    }
    loadSelectionCriteriaTemplate();
  }, [component_id, projectId, totalRegisteredHint]);

  const searchFilteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const score = r?.criteria_result && typeof r.criteria_result === "object" ? r.criteria_result?.score : "";
      const d = r?.beneficiary_details || {};
      return (
        (r?.name || "").toLowerCase().includes(q) ||
        (r?.telephone || "").toLowerCase().includes(q) ||
        (d?.district_name || "").toLowerCase().includes(q) ||
        (d?.village_name || "").toLowerCase().includes(q) ||
        (d?.camp_name || "").toLowerCase().includes(q) ||
        String(score || "").toLowerCase().includes(q)
      );
    });
  }, [rows, search]);

  const filteredRows = useMemo(() => {
    if (!selectedCampFilter) return searchFilteredRows;
    return searchFilteredRows.filter((r) => getCampName(r) === selectedCampFilter);
  }, [searchFilteredRows, selectedCampFilter]);

  const sortedRows = useMemo(() => {
    const list = [...filteredRows];
    const getValue = (r, field) => {
      const d = r?.beneficiary_details || {};
      if (field === "district") return d?.district_name || "";
      if (field === "village") return d?.village_name || "";
      if (field === "camp") return d?.camp_name || "";
      if (field === "gender") return d?.gender || "";
      if (field === "criteria_score") {
        return r?.criteria_result && typeof r.criteria_result === "object" ? Number(r.criteria_result?.score || 0) : -1;
      }
      if (field === "registered_by") return r?.registered_by?.name || "";
      return r?.[field] ?? "";
    };

    list.sort((a, b) => {
      const aVal = getValue(a, sortField);
      const bVal = getValue(b, sortField);
      const aNum = typeof aVal === "number";
      const bNum = typeof bVal === "number";
      if (aNum && bNum) {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }
      const x = String(aVal).toLowerCase();
      const y = String(bVal).toLowerCase();
      if (x < y) return sortDirection === "asc" ? -1 : 1;
      if (x > y) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [filteredRows, sortField, sortDirection]);

  const locationModalRows = useMemo(() => {
    const terms = locationModalSearch
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    let list = [...rows];

    if (terms.length) {
      list = list.filter((r) => {
        const d = r?.beneficiary_details || {};
        const haystack = [
          r?.name,
          r?.telephone,
          d?.village_name,
          d?.camp_name,
          d?.district_name,
          r?.registered_on,
        ]
          .map((v) => String(v || "").toLowerCase())
          .join(" ");

        return terms.some((term) => haystack.includes(term));
      });
    }

    const getValue = (r, field) => {
      const d = r?.beneficiary_details || {};
      if (field === "village") return d?.village_name || "";
      if (field === "camp") return d?.camp_name || "";
      if (field === "registered_on") {
        const raw = r?.registered_on;
        if (!raw) return 0;
        const ts = new Date(raw).getTime();
        return Number.isNaN(ts) ? 0 : ts;
      }
      return r?.[field] ?? "";
    };

    list.sort((a, b) => {
      const aVal = getValue(a, locationModalSortField);
      const bVal = getValue(b, locationModalSortField);
      if (typeof aVal === "number" && typeof bVal === "number") {
        return locationModalSortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }
      const x = String(aVal).toLowerCase();
      const y = String(bVal).toLowerCase();
      if (x < y) return locationModalSortDirection === "asc" ? -1 : 1;
      if (x > y) return locationModalSortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return list;
  }, [rows, locationModalSearch, locationModalSortField, locationModalSortDirection]);

  const campStats = useMemo(() => {
    if (campStatsFromApi?.length && !search.trim()) return campStatsFromApi;
    const counts = {};
    searchFilteredRows.forEach((r) => {
      const camp = getCampName(r);
      counts[camp] = (counts[camp] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([camp_name, count]) => ({ camp_name, count }))
      .sort((a, b) => b.count - a.count);
  }, [campStatsFromApi, searchFilteredRows, search]);

  const allCampsTotal = useMemo(() => {
    if (useServerPagination && paginationMeta.total && !search.trim() && campStatsFromApi?.length) {
      return paginationMeta.total;
    }
    if (campStatsFromApi?.length && !search.trim()) {
      return campStatsFromApi.reduce((sum, c) => sum + c.count, 0);
    }
    if (campStats.length) {
      return campStats.reduce((sum, c) => sum + c.count, 0);
    }
    return searchFilteredRows.length;
  }, [
    useServerPagination,
    paginationMeta.total,
    campStatsFromApi,
    campStats,
    searchFilteredRows.length,
    search,
  ]);

  const selectCampFilter = (campName) => {
    setSelectedCampFilter(campName);
    setCurrentPage(1);
  };

  const clearCampFilter = () => {
    setSelectedCampFilter(null);
    setCurrentPage(1);
  };

  const campCardsAnimKey = useMemo(
    () => campStats.map((c) => `${c.camp_name}:${c.count}`).join("|") || "empty",
    [campStats]
  );

  const useClientTablePagination =
    !useServerPagination || !!selectedCampFilter || !!search.trim();

  const paginatedRows = useMemo(() => {
    if (!useClientTablePagination) return sortedRows;
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedRows.slice(start, start + PAGE_SIZE);
  }, [sortedRows, currentPage, useClientTablePagination]);

  const displayTotal =
    !hasLoadedBeneficiaries && totalRegisteredHint > LARGE_LIST_THRESHOLD
      ? totalRegisteredHint
      : useServerPagination && !selectedCampFilter && !search.trim()
      ? paginationMeta.total
      : sortedRows.length;
  const lastPage = useMemo(() => {
    if (!useClientTablePagination) return paginationMeta.last_page || 1;
    return Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  }, [useClientTablePagination, paginationMeta.last_page, sortedRows.length]);

  useEffect(() => {
    if (!useServerPagination && currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
  }, [useServerPagination, currentPage, lastPage]);

  useEffect(() => {
    if (!useServerPagination) {
      setCurrentPage(1);
    }
  }, [search, useServerPagination]);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > lastPage || isLoading) return;
    if (!useClientTablePagination) {
      loadBeneficiaries({ page: newPage });
    } else {
      setCurrentPage(newPage);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderPaginationBar = (placement) => {
    if (isLoading || lastPage <= 1) return null;
    return (
      <div
        className={`d-flex justify-content-between align-items-center flex-wrap gap-2 px-3 py-3 ${
          placement === "top" ? "border-bottom" : "border-top"
        }`}
        style={{ backgroundColor: "#fafbfc" }}
      >
        <small className="text-muted">
          Page {currentPage} of {lastPage} ({PAGE_SIZE} per page, {displayTotal.toLocaleString()} total)
        </small>
        <nav aria-label={`Beneficiaries pagination ${placement}`}>
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${currentPage === 1 || isLoading ? "disabled" : ""}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || isLoading}
              >
                First
              </button>
            </li>
            <li className={`page-item ${currentPage === 1 || isLoading ? "disabled" : ""}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
              >
                &laquo;
              </button>
            </li>
            <li className="page-item active">
              <span className="page-link">
                {currentPage} / {lastPage}
              </span>
            </li>
            <li className={`page-item ${currentPage >= lastPage || isLoading ? "disabled" : ""}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= lastPage || isLoading}
              >
                &raquo;
              </button>
            </li>
            <li className={`page-item ${currentPage >= lastPage || isLoading ? "disabled" : ""}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => handlePageChange(lastPage)}
                disabled={currentPage >= lastPage || isLoading}
              >
                Last
              </button>
            </li>
          </ul>
        </nav>
      </div>
    );
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortDirection("asc");
  };

  const sortMark = (field) => (sortField === field ? (sortDirection === "asc" ? " ▲" : " ▼") : "");

  const getNormalizedPhone = (v) => String(v || "").replace(/\D/g, "");
  const hasOneDigitDifference = (a, b) => {
    if (!a || !b || a.length !== b.length) return false;
    let diff = 0;
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) diff += 1;
      if (diff > 1) return false;
    }
    return diff === 1;
  };

  const similarPhoneSet = useMemo(() => {
    const set = new Set();
    const phones = sortedRows.map((r) => ({
      id: r?.id,
      phone: getNormalizedPhone(r?.telephone),
    }));
    for (let i = 0; i < phones.length; i += 1) {
      for (let j = i + 1; j < phones.length; j += 1) {
        if (hasOneDigitDifference(phones[i].phone, phones[j].phone)) {
          if (phones[i].id) set.add(phones[i].id);
          if (phones[j].id) set.add(phones[j].id);
        }
      }
    }
    return set;
  }, [sortedRows]);

  const getSimilarBeneficiaries = (targetRow) => {
    if (!targetRow) return [];
    const targetPhone = getNormalizedPhone(targetRow?.telephone);
    return sortedRows.filter((r) => {
      if (!r?.id || r.id === targetRow?.id) return false;
      const p = getNormalizedPhone(r?.telephone);
      return hasOneDigitDifference(targetPhone, p);
    });
  };

  const getMealActionLabel = (actionRaw) => {
    if (actionRaw === 10001) return "accepted";
    if (actionRaw === 10002) return "rejected";
    return "-";
  };

  const visibleIds = useMemo(() => paginatedRows.map((r) => r?.id).filter(Boolean), [paginatedRows]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const toggleSelectOne = (id) => {
    if (!id) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const openVerifyFlow = () => {
    if (!canVerifyMeal) {
      cogoToast.error("You do not have permission to verify beneficiaries as MEAL officer.", { hideAfter: 4 });
      return;
    }
    if (!isVerifyMode) {
      setIsVerifyMode(true);
      return;
    }
    if (selectedIds.length === 0) {
      cogoToast.error("Please select at least one beneficiary", { hideAfter: 4 });
      return;
    }
    setShowVerifyModal(true);
  };

  const submitVerify = () => {
    if (!verifyAction) {
      cogoToast.error("Please select action", { hideAfter: 4 });
      return;
    }
    if (!verifyComment.trim()) {
      cogoToast.error("Please write comment", { hideAfter: 4 });
      return;
    }

    setIsSubmittingVerify(true);
    genericPost(ENDPOINTS.MEAL_VERIFICATION, {
      beneficiary_ids: selectedIds,
      meal_action: Number(verifyAction),
      meal_comments: verifyComment.trim(),
    })
      .then((res) => {
        setIsSubmittingVerify(false);
        if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
          cogoToast.success(res?.data?.message || `Meal verification submitted for ${selectedIds.length} beneficiary(ies).`, {
            hideAfter: 4,
          });

          // Update list locally so status icons reflect latest action without full reload
          setRows((prev) =>
            prev.map((r) =>
              selectedIds.includes(r?.id)
                ? {
                    ...r,
                    meal_action: Number(verifyAction),
                    meal_comments: verifyComment.trim(),
                    meal_verification_date: new Date().toISOString().slice(0, 19).replace("T", " "),
                  }
                : r
            )
          );

          setShowVerifyModal(false);
          setVerifyComment("");
          setVerifyAction("");
          setSelectedIds([]);
          setIsVerifyMode(false);
        } else {
          cogoToast.error(res?.data?.message || "Failed to submit meal verification", { hideAfter: 5 });
        }
      })
      .catch((e) => {
        setIsSubmittingVerify(false);
        console.error("Meal verification submit error", e);
        cogoToast.error("Failed to submit meal verification", { hideAfter: 5 });
      });
  };

  const openUpdateLocationFlow = () => {
    if (!canUpdateBeneficiaryLocation) {
      cogoToast.error("You do not have permission to update beneficiary location.", { hideAfter: 4 });
      return;
    }
    setSelectedLocationBeneficiaryIds([]);
    setSelectedLocation(null);
    setLocationModalSearch("");
    setLocationModalSortField("name");
    setLocationModalSortDirection("asc");
    setUpdateLocationStep(1);
    setShowUpdateLocationModal(true);
  };

  const closeUpdateLocationFlow = () => {
    if (isSubmittingLocationUpdate) return;
    setShowUpdateLocationModal(false);
    setUpdateLocationStep(1);
    setSelectedLocationBeneficiaryIds([]);
    setSelectedLocation(null);
    setLocationModalSearch("");
    setLocationModalSortField("name");
    setLocationModalSortDirection("asc");
  };

  const toggleUpdateLocationSelectOne = (id) => {
    if (!id) return;
    setSelectedLocationBeneficiaryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleLocationModalSort = (field) => {
    if (locationModalSortField === field) {
      setLocationModalSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setLocationModalSortField(field);
    setLocationModalSortDirection("asc");
  };

  const locationModalSortMark = (field) =>
    locationModalSortField === field
      ? locationModalSortDirection === "asc"
        ? " ▲"
        : " ▼"
      : "";

  const allUpdateLocationVisibleSelected =
    locationModalRows.length > 0 &&
    locationModalRows.every((r) => r?.id && selectedLocationBeneficiaryIds.includes(r.id));

  const toggleUpdateLocationSelectAll = () => {
    const visible = locationModalRows.map((r) => r?.id).filter(Boolean);
    if (!visible.length) return;
    if (allUpdateLocationVisibleSelected) {
      setSelectedLocationBeneficiaryIds((prev) => prev.filter((id) => !visible.includes(id)));
      return;
    }
    setSelectedLocationBeneficiaryIds((prev) => Array.from(new Set([...prev, ...visible])));
  };

  const proceedToLocationPicker = () => {
    if (!selectedLocationBeneficiaryIds.length) {
      cogoToast.error("Please select at least one beneficiary", { hideAfter: 4 });
      return;
    }
    setShowUpdateLocationModal(false);
    setIsLocationPickerOpen(true);
  };

  const submitBeneficiaryLocationUpdate = () => {
    if (!canUpdateBeneficiaryLocation) {
      cogoToast.error("You do not have permission to update beneficiary location.", { hideAfter: 4 });
      return;
    }
    if (!selectedLocationBeneficiaryIds.length) {
      cogoToast.error("No beneficiaries selected", { hideAfter: 4 });
      return;
    }
    if (!selectedLocation?.country) {
      cogoToast.error("Please select a location", { hideAfter: 4 });
      return;
    }

    setIsSubmittingLocationUpdate(true);
    genericPost(ENDPOINTS.UPDATE_BENEFICIARY_LOCATIONS, {
      beneficiary_ids: selectedLocationBeneficiaryIds,
      location: selectedLocation,
    })
      .then((res) => {
        setIsSubmittingLocationUpdate(false);
        if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
          cogoToast.success(
            res?.data?.message ||
              `Location updated for ${selectedLocationBeneficiaryIds.length} beneficiary(ies).`,
            { hideAfter: 4 }
          );
          closeUpdateLocationFlow();
          return;
        }
        cogoToast.error(res?.data?.message || "Failed to update beneficiary locations", { hideAfter: 5 });
      })
      .catch((e) => {
        setIsSubmittingLocationUpdate(false);
        console.error("Beneficiary location update error", e);
        cogoToast.error("Failed to update beneficiary locations", { hideAfter: 5 });
      });
  };

  const applyLocationFilter = (locationPayload) => {
    setAppliedLocationFilter(locationPayload);
    setSelectedCampFilter(null);
    setCurrentPage(1);
    setHasLoadedBeneficiaries(true);
    loadBeneficiaries({ locationFilter: locationPayload, page: 1 });
  };

  const clearLocationFilter = () => {
    setAppliedLocationFilter(null);
    setSelectedCampFilter(null);
    setCurrentPage(1);
    if (hasLoadedBeneficiaries) {
      loadBeneficiaries({ locationFilter: null, page: 1 });
    }
  };

  const applyDateFilter = () => {
    if (!startDate || !endDate) {
      cogoToast.error("Please select both start and end dates", { hideAfter: 4 });
      return;
    }
    if (startDate > endDate) {
      cogoToast.error("Start date cannot be after end date", { hideAfter: 4 });
      return;
    }
    const dateFilter = { start_date: startDate, end_date: endDate };
    setAppliedDateFilter(dateFilter);
    setSelectedCampFilter(null);
    setCurrentPage(1);
    setHasLoadedBeneficiaries(true);
    loadBeneficiaries({ dateFilter, page: 1 });
  };

  const viewAllBeneficiaries = () => {
    setStartDate("");
    setEndDate("");
    setAppliedDateFilter(null);
    setAppliedLocationFilter(null);
    setSelectedCampFilter(null);
    setCurrentPage(1);
    setHasLoadedBeneficiaries(true);
    loadBeneficiaries({ dateFilter: null, locationFilter: null, page: 1 });
  };

  const openMasterListModal = () => {
    if (!projectId || !component_id) {
      cogoToast.error("Project or component information is missing.", { hideAfter: 4 });
      return;
    }
    setMasterListForm({
      cycle_id: "",
      masterlist_type: "",
      template_status: "new",
      start_date: "",
      end_date: "",
    });
    setShowMasterListModal(true);
    setMasterListCyclesLoading(true);
    genericPost(ENDPOINTS.PROJECT_CYCLES, {
      project_id: projectId,
      component_id,
    })
      .then((res) => {
        setMasterListCyclesLoading(false);
        if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
          setMasterListCycles(Array.isArray(res.data.data) ? res.data.data : []);
        } else {
          setMasterListCycles([]);
          cogoToast.error(res?.data?.message || "Failed to load component cycles", { hideAfter: 4 });
        }
      })
      .catch((err) => {
        setMasterListCyclesLoading(false);
        setMasterListCycles([]);
        console.error("Load cycles error", err);
        cogoToast.error("Failed to load component cycles", { hideAfter: 4 });
      });
  };

  const closeMasterListModal = () => {
    setShowMasterListModal(false);
  };

  const goToFailedCriterion = () => {
    if (!component_id) {
      cogoToast.error("Component information is missing.", { hideAfter: 4 });
      return;
    }
    history.push({
      pathname: "/projects/failed-criterion",
      state: {
        component_id,
        component_name: activityName,
        project_name: projectName,
      },
    });
  };

  const handleMasterListFormChange = (field, value) => {
    setMasterListForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerateMasterList = (e) => {
    e.preventDefault();
    if (!masterListForm.cycle_id || !masterListForm.masterlist_type) {
      cogoToast.error("Please select cycle and master list type.", { hideAfter: 4 });
      return;
    }
    if (!projectId || !component_id) {
      cogoToast.error("Project or component information is missing.", { hideAfter: 4 });
      return;
    }

    const payload = {
      project_id: projectId,
      component_id,
      component_cycle_id: masterListForm.cycle_id,
      masterlist_type: masterListForm.masterlist_type,
      template_status: masterListForm.template_status,
      start_date: masterListForm.start_date,
      end_date: masterListForm.end_date,
    };
    if (appliedLocationFilter?.country) {
      payload.location = appliedLocationFilter;
    }

    setIsGeneratingMasterList(true);
    genericPost(ENDPOINTS.EXPORT_DATA, payload)
      .then((res) => {
        setIsGeneratingMasterList(false);
        if (
          res?.status === 200 &&
          (res?.data?.status === SUCCESS_RESULT || res?.data?.status === DEFAULT_RESULT)
        ) {
          const jobData = res?.data?.data || null;
          cogoToast.success(res?.data?.message || "Masterlist export queued successfully.", {
            hideAfter: 4,
          });
          closeMasterListModal();
          if (jobData?.queue_job_id) {
            history.push({
              pathname: `/projects/masterlist-job-tracker/${encodeURIComponent(jobData.queue_job_id)}`,
              state: { job: jobData },
            });
          } else {
            cogoToast.error("Masterlist queued but job id was not returned.", { hideAfter: 5 });
          }
        } else {
          cogoToast.error(res?.data?.message || "Failed to queue masterlist export", { hideAfter: 4 });
        }
      })
      .catch((err) => {
        setIsGeneratingMasterList(false);
        console.error("Masterlist export error", err);
        cogoToast.error("An error occurred while queueing masterlist export.", { hideAfter: 4 });
      });
  };

  const openDistributionSummary = () => {
    if (!component_id) return;
    setShowDistributionSummaryModal(true);
    setDistributionSummaryLoading(true);
    setDistributionSummaryCycles([]);
    genericPost(ENDPOINTS.DISTRIBUTIONS_SUMMARY, { component_id })
      .then((res) => {
        setDistributionSummaryLoading(false);
        const apiStatus = res?.data?.status;
        if (
          res?.status === 200 &&
          (apiStatus === SUCCESS_RESULT || apiStatus === DEFAULT_RESULT)
        ) {
          const cycles = res?.data?.data?.cycles;
          setDistributionSummaryCycles(Array.isArray(cycles) ? cycles : []);
        } else {
          cogoToast.error(res?.data?.message || "Failed to load distribution summary", { hideAfter: 4 });
        }
      })
      .catch((e) => {
        setDistributionSummaryLoading(false);
        console.error("Distribution summary load error", e);
        cogoToast.error("Failed to load distribution summary", { hideAfter: 4 });
      });
  };

  const closeDistributionSummary = () => {
    setShowDistributionSummaryModal(false);
    setDistributionSummaryCycles([]);
  };

  const appliedLocationLabel = [
    appliedLocationFilter?.labels?.country,
    appliedLocationFilter?.labels?.zone,
    appliedLocationFilter?.labels?.district,
    appliedLocationFilter?.labels?.village,
    appliedLocationFilter?.labels?.camp,
  ]
    .filter(Boolean)
    .join(" / ");

  return (
    <div style={{ padding: "20px", minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <Breadcrumb className="ms-1 mb-3">
        <BreadcrumbItem><Link to="/dashboard">Dashboard</Link></BreadcrumbItem>
        <BreadcrumbItem><Link to="/projects/list">Projects</Link></BreadcrumbItem>
        <BreadcrumbItem><Link to={`/projects/details/${projectId || ""}`}>Project Details</Link></BreadcrumbItem>
        <BreadcrumbItem active>Active Beneficiaries</BreadcrumbItem>
      </Breadcrumb>

      <Card className="border-0 shadow-sm mb-3">
        <CardHeader style={{ fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>{activityName}</span>
          {canUpdateComponentProject && (
            <Button
              color="warning"
              size="sm"
              onClick={() => {
                setSelectedNewProjectId("");
                setShowMoveComponentModal(true);
                if (allProjects.length === 0) {
                  setAllProjectsLoading(true);
                  getProjects()
                    .then((res) => {
                      const list = res?.data?.data || res?.data || [];
                      setAllProjects(Array.isArray(list) ? list : []);
                    })
                    .catch(() => cogoToast.error("Failed to load projects"))
                    .finally(() => setAllProjectsLoading(false));
                }
              }}
            >
              <FaExchangeAlt className="me-1" /> Move to Another Project
            </Button>
          )}
        </CardHeader>
        <CardBody>
          {!hasLoadedBeneficiaries && totalRegisteredHint > LARGE_LIST_THRESHOLD && (
            <div className="alert alert-info py-3 mb-3 d-flex align-items-start gap-2" style={{ fontSize: "0.9rem" }}>
              <FaExclamationCircle style={{ marginTop: 2, flexShrink: 0 }} />
              <span>
                This activity has <strong>{totalRegisteredHint.toLocaleString()}</strong> registered beneficiaries.
                Data is not loaded automatically. Use <strong>Apply Filter</strong> (date range),{" "}
                <strong>Filter by Location</strong>, or <strong>View All</strong> to load records.
              </span>
            </div>
          )}
          {rows.length > 0 && (
            <div className="alert alert-warning py-2 mb-3 d-flex align-items-center gap-2" style={{ fontSize: "0.85rem" }}>
              <FaExclamationTriangle />
              <span>This page contains sensitive beneficiary data. Please do not export, print, or share with anyone.</span>
            </div>
          )}
          <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
            <div className="d-flex flex-wrap align-items-end gap-3">
            <div>
              <label className="form-label mb-1" style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                Start Date
              </label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ minWidth: 160 }}
              />
            </div>
            <div>
              <label className="form-label mb-1" style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                End Date
              </label>
              <Input
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ minWidth: 160 }}
              />
            </div>
            <Button color="primary" size="sm" onClick={applyDateFilter} disabled={isLoading}>
              Apply Filter
            </Button>
            <button
              type="button"
              className="d-flex align-items-center gap-1"
              onClick={() => setIsFilterLocationPickerOpen(true)}
              disabled={isLoading}
              style={{
                padding: "0.375rem 0.75rem",
                fontSize: "0.875rem",
                lineHeight: 1.5,
                borderRadius: "0.25rem",
                backgroundColor: "#ffffff",
                border: "1px solid #ced4da",
                color: "#495057",
                boxShadow: "none",
                cursor: isLoading ? "not-allowed" : "pointer",
                opacity: isLoading ? 0.65 : 1,
              }}
            >
              <FaMapMarkerAlt style={{ color: "#0778D4" }} />
              Filter by Location
            </button>
            {!!appliedLocationFilter && (
              <Button color="secondary" size="sm" outline onClick={clearLocationFilter} disabled={isLoading}>
                Clear Location Filter
              </Button>
            )}
            <Button color="secondary" size="sm" outline onClick={viewAllBeneficiaries} disabled={isLoading}>
              View All
            </Button>
            </div>
            <div className="ms-auto d-flex align-items-center gap-2">
              {canViewFailedCriterion && (
                <Button
                  color="secondary"
                  size="sm"
                  outline
                  className="d-flex align-items-center gap-1"
                  onClick={goToFailedCriterion}
                  disabled={!component_id}
                >
                  <FaExclamationCircle />
                  View Failed Criterion
                </Button>
              )}
              {canGenerateMasterList && (
                <Button
                  color="info"
                  size="sm"
                  className="d-flex align-items-center gap-1"
                  onClick={openMasterListModal}
                  disabled={!component_id || !projectId}
                >
                  <FaFileDownload />
                  Generate Master List
                </Button>
              )}
              <Button
                color="info"
                size="sm"
                className="d-flex align-items-center gap-1"
                onClick={openDistributionSummary}
                disabled={!component_id}
              >
                <FaChartBar />
                View Distribution Summary
              </Button>
            </div>
          </div>
          <Input
            type="text"
            placeholder="Search by name, phone, district, village, score..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </CardBody>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
            <span style={{ fontWeight: 700 }}>
              Beneficiary List ({displayTotal.toLocaleString()}
              {search.trim() && !useServerPagination ? ` filtered, ${sortedRows.length.toLocaleString()} shown` : ""})
            </span>
            {!!appliedDateFilter && (
              <Badge color="primary" pill style={{ fontSize: "0.72rem", padding: "6px 10px" }}>
                Dates: {appliedDateFilter.start_date} to {appliedDateFilter.end_date}
              </Badge>
            )}
            {!!appliedLocationLabel && (
              <Badge color="info" pill style={{ fontSize: "0.72rem", padding: "6px 10px" }}>
                Filter: {appliedLocationLabel}
              </Badge>
            )}
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Badge color="warning" pill style={{ fontSize: "0.78rem", padding: "6px 10px" }}>
              Similar phone numbers: {similarPhoneSet.size}
            </Badge>
            {canUpdateBeneficiaryLocation && (
              <Button color="info" size="sm" onClick={openUpdateLocationFlow}>
                Update Beneficiary Location
              </Button>
            )}
            {canVerifyMeal && (
              <>
                <Button color={isVerifyMode ? "success" : "primary"} size="sm" onClick={openVerifyFlow}>
                  {isVerifyMode ? `Verify Selected (${selectedIds.length})` : "Verify as Meal"}
                </Button>
                {isVerifyMode && (
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={() => {
                      setIsVerifyMode(false);
                      setSelectedIds([]);
                    }}
                  >
                    Cancel Selection
                  </Button>
                )}
              </>
            )}
          </div>
        </CardHeader>
        <CardBody style={{ padding: 0 }}>
          {(isLoading || (hasLoadedBeneficiaries && campStats.length > 0)) && (
            <div
              style={{
                padding: "12px 14px",
                borderBottom: "1px solid #e9ecef",
                background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
              }}
            >
              <style>
                {`
                  @keyframes campCardEnter {
                    0% {
                      opacity: 0;
                      transform: translateY(18px) scale(0.9);
                    }
                    65% {
                      transform: translateY(-3px) scale(1.03);
                    }
                    100% {
                      opacity: 1;
                      transform: translateY(0) scale(1);
                    }
                  }
                  @keyframes campSkeletonPulse {
                    0%, 100% { opacity: 0.45; transform: scale(0.98); }
                    50% { opacity: 0.85; transform: scale(1); }
                  }
                  .camp-stat-card-animate {
                    animation: campCardEnter 0.42s cubic-bezier(0.22, 1, 0.36, 1) forwards;
                    opacity: 0;
                  }
                  .camp-stat-card-animate:hover {
                    transform: translateY(-2px) scale(1.03);
                    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.22) !important;
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                  }
                  .camp-skeleton-animate {
                    animation: campSkeletonPulse 0.9s ease-in-out infinite;
                  }
                `}
              </style>
              <div
                className="d-flex align-items-center justify-content-between mb-2"
                style={{ gap: 8 }}
              >
                <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#475569", letterSpacing: "0.03em" }}>
                  BY CAMP
                </span>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {selectedCampFilter && (
                    <Badge color="dark" pill style={{ fontSize: "0.68rem", padding: "5px 10px" }}>
                      Filter: {selectedCampFilter}
                    </Badge>
                  )}
                  {!campStatsFromApi?.length && useServerPagination && !isLoading && (
                    <span style={{ fontSize: "0.68rem", color: "#94a3b8" }}>Counts from loaded page</span>
                  )}
                </div>
              </div>
              <div
                key={campCardsAnimKey}
                style={{
                  display: "flex",
                  gap: 10,
                  overflowX: "auto",
                  overflowY: "hidden",
                  paddingBottom: 4,
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "thin",
                }}
              >
                {isLoading
                  ? [1, 2, 3, 4, 5, 6].map((s) => (
                      <div
                        key={`camp-skel-${s}`}
                        className="camp-skeleton-animate"
                        style={{
                          flex: "0 0 auto",
                          width: 132,
                          height: 72,
                          borderRadius: 10,
                          background: "linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)",
                          animationDelay: `${(s - 1) * CAMP_CARD_STAGGER_MS}ms`,
                        }}
                      />
                    ))
                  : (
                    <>
                      <div
                        key="all-camps"
                        role="button"
                        tabIndex={0}
                        title="Show all camps"
                        className="camp-stat-card-animate"
                        onClick={clearCampFilter}
                        onKeyDown={(e) => e.key === "Enter" && clearCampFilter()}
                        style={{
                          flex: "0 0 auto",
                          minWidth: 128,
                          maxWidth: 180,
                          padding: "12px 14px",
                          borderRadius: 10,
                          background: ALL_CAMPS_GRADIENT,
                          border: "none",
                          boxShadow: selectedCampFilter
                            ? "0 4px 12px rgba(15, 23, 42, 0.18)"
                            : "0 6px 16px rgba(15, 23, 42, 0.28)",
                          textAlign: "center",
                          cursor: "pointer",
                          animationDelay: "0ms",
                          outline: !selectedCampFilter ? "3px solid #ffffff" : "none",
                          outlineOffset: !selectedCampFilter ? 2 : 0,
                          transform: !selectedCampFilter ? "scale(1.05)" : undefined,
                        }}
                      >
                        <div
                          style={{
                            fontSize: "0.88rem",
                            fontWeight: 900,
                            color: "#ffffff",
                            lineHeight: 1.35,
                            width: "100%",
                            textAlign: "center",
                            textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                          }}
                        >
                          All Camps
                        </div>
                        <div
                          style={{
                            fontSize: "1.35rem",
                            fontWeight: 900,
                            color: "#ffffff",
                            marginTop: 8,
                            lineHeight: 1,
                            textAlign: "center",
                            textShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
                          }}
                        >
                          {allCampsTotal.toLocaleString()}
                        </div>
                      </div>
                      {campStats.map((camp, i) => {
                        const gradient = CAMP_CARD_GRADIENTS[i % CAMP_CARD_GRADIENTS.length];
                        const isSelected = selectedCampFilter === camp.camp_name;
                        return (
                          <div
                            key={`${camp.camp_name}-${i}`}
                            role="button"
                            tabIndex={0}
                            title={`Filter by ${camp.camp_name}`}
                            className="camp-stat-card-animate"
                            onClick={() => selectCampFilter(camp.camp_name)}
                            onKeyDown={(e) => e.key === "Enter" && selectCampFilter(camp.camp_name)}
                            style={{
                              flex: "0 0 auto",
                              minWidth: 128,
                              maxWidth: 180,
                              padding: "12px 14px",
                              borderRadius: 10,
                              background: gradient,
                              border: "none",
                              boxShadow: isSelected
                                ? "0 6px 16px rgba(15, 23, 42, 0.28)"
                                : "0 4px 12px rgba(15, 23, 42, 0.18)",
                              textAlign: "center",
                              cursor: "pointer",
                              animationDelay: `${(i + 1) * CAMP_CARD_STAGGER_MS}ms`,
                              outline: isSelected ? "3px solid #ffffff" : "none",
                              outlineOffset: isSelected ? 2 : 0,
                              transform: isSelected ? "scale(1.05)" : undefined,
                            }}
                          >
                            <div
                              style={{
                                fontSize: "0.88rem",
                                fontWeight: 900,
                                color: "#ffffff",
                                lineHeight: 1.35,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                minHeight: "2.5em",
                                width: "100%",
                                textAlign: "center",
                                textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                              }}
                            >
                              {camp.camp_name}
                            </div>
                            <div
                              style={{
                                fontSize: "1.35rem",
                                fontWeight: 900,
                                color: "#ffffff",
                                marginTop: 1,
                                lineHeight: 1,
                                textAlign: "center",
                                textShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
                              }}
                            >
                              {camp.count.toLocaleString()}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
              </div>
            </div>
          )}
          {!hasLoadedBeneficiaries && !isLoading ? (
            <div className="text-center text-muted py-5 px-3">
              Select a filter or click <strong>View All</strong> to load beneficiaries.
            </div>
          ) : isLoading ? (
            <div className="text-center text-muted py-4">Loading active beneficiaries...</div>
          ) : (
            <>
              {renderPaginationBar("top")}
              <div className="table-responsive">
              <Table hover className="mb-0">
                <thead>
                  <tr>
                    {canVerifyMeal && isVerifyMode && (
                      <th style={{ width: 50 }}>
                        <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} />
                      </th>
                    )}
                    <th>#</th>
                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("name")}>Name{sortMark("name")}</th>
                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("telephone")}>Phone{sortMark("telephone")}</th>
                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("district")}>District{sortMark("district")}</th>
                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("village")}>Village{sortMark("village")}</th>
                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("camp")}>Camp{sortMark("camp")}</th>
                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("gender")}>Gender{sortMark("gender")}</th>
                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("criteria_score")}>Criteria Score{sortMark("criteria_score")}</th>
                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("registered_on")}>Registered On{sortMark("registered_on")}</th>
                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("registered_by")}>Registered By{sortMark("registered_by")}</th>
                    <th>Meal Verification</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((r, idx) => {
                    const score = r?.criteria_result && typeof r.criteria_result === "object"
                      ? r.criteria_result?.score
                      : "-";
                    const d = r?.beneficiary_details || {};
                    const isMealVerified = !!r?.meal_verification_date || r?.meal_action === 10001;
                    const called = r?.is_called === true;
                    const isSimilarPhone = !!r?.id && similarPhoneSet.has(r.id);
                    return (
                      <tr key={r?.id || idx} style={isSimilarPhone ? { backgroundColor: "#fff8e1" } : {}}>
                        {canVerifyMeal && isVerifyMode && (
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(r?.id)}
                              onChange={() => toggleSelectOne(r?.id)}
                            />
                          </td>
                        )}
                        <td>{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <span>{r?.name || "-"}</span>
                            {r?.is_sub_account === true && (
                              <FaChild
                                style={{ color: "#6b7280", fontSize: "0.95rem", flexShrink: 0 }}
                                title="This is child / sub account"
                              />
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <span>{r?.telephone || "-"}</span>
                            {isSimilarPhone && (
                              <Badge
                                color="warning"
                                pill
                                style={{ fontSize: "0.68rem", cursor: "pointer" }}
                                title="Click to view similar beneficiaries"
                                onClick={() => setSelectedSimilarRow(r)}
                              >
                                Similar
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td>{d?.district_name || "-"}</td>
                        <td>{d?.village_name || "-"}</td>
                        <td>{d?.camp_name || "-"}</td>
                        <td>{d?.gender || "-"}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <span>{score ?? "-"}</span>
                            {r?.criteria_result && typeof r.criteria_result === "object" && (
                              <FaInfoCircle
                                style={{
                                  color: "#007bff",
                                  cursor: "pointer",
                                  fontSize: "16px",
                                }}
                                title="View criteria summary"
                                onClick={() => setCriteriaSummaryRow(r)}
                              />
                            )}
                          </div>
                        </td>
                        <td>{r?.registered_on || "-"}</td>
                        <td>{r?.registered_by?.name || "-"}</td>
                        <td style={{ minWidth: 180 }}>
                          <div className="d-flex align-items-center gap-3">
                            <span
                              title={isMealVerified ? "Meal verified - click for details" : "Meal not verified - click for details"}
                              style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
                              onClick={() => setSelectedMealRow(r)}
                            >
                              {isMealVerified ? (
                                <FaCheckCircle style={{ color: "#16a34a", fontSize: "1rem" }} />
                              ) : (
                                <FaExclamationCircle style={{ color: "#9ca3af", fontSize: "1rem" }} />
                              )}
                            </span>
                            <span
                              title={called ? "Called" : "Not called"}
                              style={{ display: "inline-flex", alignItems: "center" }}
                            >
                              <FaPhone style={{ color: called ? "#16a34a" : "#9ca3af", fontSize: "0.95rem" }} />
                            </span>
                          
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {paginatedRows.length === 0 && (
                    <tr>
                      <td colSpan={canVerifyMeal && isVerifyMode ? 12 : 11} className="text-center text-muted py-3">
                        No active beneficiaries found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
              {renderPaginationBar("bottom")}
            </>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={!!selectedMealRow} toggle={() => setSelectedMealRow(null)}>
        <ModalHeader toggle={() => setSelectedMealRow(null)}>Meal Verification Details</ModalHeader>
        <ModalBody>
          <div style={{ fontSize: "0.9rem" }}>
            <div className="mb-2"><strong>Beneficiary:</strong> {selectedMealRow?.name || "-"}</div>
            <div className="mb-2">
              <strong>Verification Date:</strong> {selectedMealRow?.meal_verification_date || "-"}
            </div>
            <div className="mb-2"><strong>Meal User:</strong> {selectedMealRow?.meal_user || "-"}</div>
            <div className="mb-2">
              <strong>Action:</strong>{" "}
              <Badge color={selectedMealRow?.meal_action === 10001 ? "success" : selectedMealRow?.meal_action === 10002 ? "danger" : "secondary"}>
                {getMealActionLabel(selectedMealRow?.meal_action)}
              </Badge>
            </div>
            <div className="mb-2"><strong>Comments:</strong> {selectedMealRow?.meal_comments || "-"}</div>
            <div className="mb-2">
              <strong>Called:</strong> {selectedMealRow?.is_called ? "Yes" : "No"}
            </div>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={!!selectedSimilarRow} toggle={() => setSelectedSimilarRow(null)}>
        <ModalHeader toggle={() => setSelectedSimilarRow(null)}>Similar Phone Beneficiaries</ModalHeader>
        <ModalBody>
          <div className="mb-2" style={{ fontSize: "0.9rem" }}>
            <strong>Selected:</strong> {selectedSimilarRow?.name || "-"} ({selectedSimilarRow?.telephone || "-"})
          </div>
          <div className="table-responsive">
            <Table size="sm" bordered>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Beneficiary Name</th>
                  <th>Village</th>
                  <th>Camp Name</th>
                  <th>Phone</th>
                </tr>
              </thead>
              <tbody>
                {getSimilarBeneficiaries(selectedSimilarRow).map((r, idx) => {
                  const d = r?.beneficiary_details || {};
                  return (
                    <tr key={r?.id || idx}>
                      <td>{idx + 1}</td>
                      <td>{r?.name || "-"}</td>
                      <td>{d?.village_name || "-"}</td>
                      <td>{d?.camp_name || "-"}</td>
                      <td>{r?.telephone || "-"}</td>
                    </tr>
                  );
                })}
                {getSimilarBeneficiaries(selectedSimilarRow).length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted">No similar beneficiaries found.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={showVerifyModal} toggle={() => setShowVerifyModal(false)}>
        <ModalHeader toggle={() => setShowVerifyModal(false)}>Verify as Meal</ModalHeader>
        <ModalBody>
          <div className="mb-2" style={{ fontSize: "0.9rem" }}>
            Selected beneficiaries: <strong>{selectedIds.length}</strong>
          </div>
          <div className="mb-3">
            <label style={{ fontWeight: 600, marginBottom: 6 }}>Comment</label>
            <textarea
              className="form-control"
              rows={3}
              value={verifyComment}
              onChange={(e) => setVerifyComment(e.target.value)}
              placeholder="Write comment..."
            />
          </div>
          <div>
            <label style={{ fontWeight: 600, marginBottom: 6 }}>Action</label>
            <select className="form-control" value={verifyAction} onChange={(e) => setVerifyAction(e.target.value)}>
              <option value="">Select action</option>
              <option value="10001">10001 - Accept</option>
              <option value="10002">10002 - Reject</option>
            </select>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowVerifyModal(false)} disabled={isSubmittingVerify}>Cancel</Button>
          <Button color="primary" onClick={submitVerify} disabled={isSubmittingVerify}>
            {isSubmittingVerify ? "Submitting..." : "Submit"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={showUpdateLocationModal} toggle={closeUpdateLocationFlow} size="lg">
        <ModalHeader toggle={closeUpdateLocationFlow}>
          {updateLocationStep === 1 ? "Select Beneficiaries" : "Confirm Location Update"}
        </ModalHeader>
        <ModalBody>
          {updateLocationStep === 1 ? (
            <>
              <div className="mb-2" style={{ fontSize: "0.9rem" }}>
                Select beneficiaries to update location.
              </div>
              <div className="mb-2">
                <Input
                  type="text"
                  bsSize="sm"
                  placeholder="Search… use commas for multiple values (e.g. 676271,7281728,878872)"
                  value={locationModalSearch}
                  onChange={(e) => setLocationModalSearch(e.target.value)}
                />
              </div>
              <div className="table-responsive" style={{ maxHeight: 380 }}>
                <Table size="sm" bordered hover>
                  <thead>
                    <tr>
                      <th style={{ width: 50 }}>
                        <input
                          type="checkbox"
                          checked={allUpdateLocationVisibleSelected}
                          onChange={toggleUpdateLocationSelectAll}
                        />
                      </th>
                      <th>#</th>
                      <th
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => handleLocationModalSort("name")}
                      >
                        Name{locationModalSortMark("name")}
                      </th>
                      <th
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => handleLocationModalSort("telephone")}
                      >
                        Phone{locationModalSortMark("telephone")}
                      </th>
                      <th
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => handleLocationModalSort("village")}
                      >
                        Village{locationModalSortMark("village")}
                      </th>
                      <th
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => handleLocationModalSort("camp")}
                      >
                        Camp{locationModalSortMark("camp")}
                      </th>
                      <th
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => handleLocationModalSort("registered_on")}
                      >
                        Registration Date{locationModalSortMark("registered_on")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {locationModalRows.map((r, idx) => {
                      const d = r?.beneficiary_details || {};
                      return (
                        <tr key={r?.id || idx}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedLocationBeneficiaryIds.includes(r?.id)}
                              onChange={() => toggleUpdateLocationSelectOne(r?.id)}
                            />
                          </td>
                          <td>{idx + 1}</td>
                          <td>{r?.name || "-"}</td>
                          <td>{r?.telephone || "-"}</td>
                          <td>{d?.village_name || "-"}</td>
                          <td>{d?.camp_name || "-"}</td>
                          <td>{r?.registered_on || "-"}</td>
                        </tr>
                      );
                    })}
                    {locationModalRows.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center text-muted">
                          No beneficiaries found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
              <div className="mt-2 d-flex justify-content-between flex-wrap gap-2">
                <div>
                  Selected: <strong>{selectedLocationBeneficiaryIds.length}</strong>
                </div>
                <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                  Showing {locationModalRows.length.toLocaleString()} of {rows.length.toLocaleString()}
                </div>
              </div>
            </>
          ) : (
            <div style={{ fontSize: "0.95rem" }}>
              <div className="mb-2">
                You are updating location for{" "}
                <strong>{selectedLocationBeneficiaryIds.length}</strong> beneficiary(ies).
              </div>
              <div className="mb-2">
                <strong>New location:</strong>
              </div>
              <ul style={{ marginBottom: 0 }}>
                <li>Country: {selectedLocation?.labels?.country || selectedLocation?.country || "-"}</li>
                <li>Zone: {selectedLocation?.labels?.zone || selectedLocation?.zone || "-"}</li>
                <li>District: {selectedLocation?.labels?.district || selectedLocation?.district || "-"}</li>
                <li>Village: {selectedLocation?.labels?.village || selectedLocation?.village || "-"}</li>
                <li>Camp: {selectedLocation?.labels?.camp || selectedLocation?.camp || "-"}</li>
              </ul>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {updateLocationStep === 1 ? (
            <>
              <Button color="secondary" onClick={closeUpdateLocationFlow}>
                Cancel
              </Button>
              <Button color="primary" onClick={proceedToLocationPicker}>
                Next
              </Button>
            </>
          ) : (
            <>
              <Button
                color="secondary"
                onClick={() => {
                  setUpdateLocationStep(1);
                }}
                disabled={isSubmittingLocationUpdate}
              >
                Back
              </Button>
              <Button color="success" onClick={submitBeneficiaryLocationUpdate} disabled={isSubmittingLocationUpdate}>
                {isSubmittingLocationUpdate ? "Submitting..." : "Submit"}
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>

      <LocationPickerModal
        isOpen={isLocationPickerOpen}
        toggle={() => setIsLocationPickerOpen(false)}
        onSelect={(payload) => {
          setSelectedLocation(payload);
          setIsLocationPickerOpen(false);
          setUpdateLocationStep(2);
          setShowUpdateLocationModal(true);
        }}
      />

      <LocationPickerModal
        isOpen={isFilterLocationPickerOpen}
        toggle={() => setIsFilterLocationPickerOpen(false)}
        title="Filter Beneficiaries by Location"
        initialValue={appliedLocationFilter}
        onSelect={(payload) => {
          setIsFilterLocationPickerOpen(false);
          applyLocationFilter(payload);
        }}
      />

      <CriteriaSummaryModal
        isOpen={!!criteriaSummaryRow}
        toggle={() => setCriteriaSummaryRow(null)}
        criteriaSummary={criteriaSummaryRow?.criteria_result || criteriaSummaryRow?.criteria_summary}
        criteriaTemplate={selectionCriteriaTemplate}
        beneficiaryName={criteriaSummaryRow?.name}
      />

      <Modal isOpen={showDistributionSummaryModal} toggle={closeDistributionSummary} size="lg" style={{ maxWidth: "920px" }}>
        <ModalHeader toggle={closeDistributionSummary}>
          <div className="d-flex align-items-center gap-2">
            <FaChartBar style={{ color: "#0d6efd", fontSize: "1.25rem" }} />
            <span>Distribution Summary</span>
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: "1.25rem", maxHeight: "70vh", overflowY: "auto" }}>
          {distributionSummaryLoading ? (
            <div className="text-center text-muted py-5">Loading distribution summary...</div>
          ) : distributionSummaryCycles.length === 0 ? (
            <div className="text-center text-muted py-5">No distribution cycles found for this activity.</div>
          ) : (
            <div style={{ display: "grid", gap: "14px" }}>
              {distributionSummaryCycles.map((cycle, idx) => {
                const distributed = Number(cycle?.distributed) || 0;
                const notDistributed = Number(cycle?.not_distributed) || 0;
                const totalEligible = Number(cycle?.total_eligible) || 0;
                const rawPct = totalEligible > 0 ? (distributed / totalEligible) * 100 : 0;
                const barPct = Math.min(100, rawPct);
                const pctLabel =
                  totalEligible === 0
                    ? "0"
                    : notDistributed === 0
                      ? "100"
                      : rawPct.toFixed(1);
                return (
                  <div
                    key={cycle?.cycle_id || idx}
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: "12px",
                      padding: "16px 18px",
                      background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                      boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                      <div>
                        <div style={{ fontWeight: 800, fontSize: "1.05rem", color: "#1e293b" }}>
                          {cycle?.cycle_name || "Unnamed cycle"}
                        </div>
                        {(cycle?.start_date || cycle?.end_date) && (
                          <div style={{ fontSize: "0.78rem", color: "#64748b", marginTop: 4 }}>
                            {cycle?.start_date || "—"} to {cycle?.end_date || "—"}
                          </div>
                        )}
                      </div>
                      <Badge color="light" style={{ color: "#475569", fontWeight: 600, border: "1px solid #e2e8f0" }}>
                        {pctLabel}% distributed
                      </Badge>
                    </div>
                    <div
                      style={{
                        height: 8,
                        borderRadius: 999,
                        backgroundColor: "#e2e8f0",
                        overflow: "hidden",
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          width: `${barPct}%`,
                          height: "100%",
                          borderRadius: 999,
                          background: "linear-gradient(90deg, #22c55e 0%, #16a34a 100%)",
                        }}
                      />
                    </div>
                    <div className="row g-2">
                      <div className="col-md-4">
                        <div
                          style={{
                            textAlign: "center",
                            padding: "12px 10px",
                            borderRadius: "10px",
                            backgroundColor: "#ecfdf5",
                            border: "1px solid #bbf7d0",
                          }}
                        >
                          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#15803d", textTransform: "uppercase" }}>
                            Distributed
                          </div>
                          <div style={{ fontSize: "1.5rem", fontWeight: 900, color: "#16a34a", marginTop: 4 }}>
                            {distributed.toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div
                          style={{
                            textAlign: "center",
                            padding: "12px 10px",
                            borderRadius: "10px",
                            backgroundColor: "#fff7ed",
                            border: "1px solid #fed7aa",
                          }}
                        >
                          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#c2410c", textTransform: "uppercase" }}>
                            Not Distributed
                          </div>
                          <div style={{ fontSize: "1.5rem", fontWeight: 900, color: "#ea580c", marginTop: 4 }}>
                            {notDistributed.toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div
                          style={{
                            textAlign: "center",
                            padding: "12px 10px",
                            borderRadius: "10px",
                            backgroundColor: "#eff6ff",
                            border: "1px solid #bfdbfe",
                          }}
                        >
                          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#1d4ed8", textTransform: "uppercase" }}>
                            Total Eligible
                          </div>
                          <div style={{ fontSize: "1.5rem", fontWeight: 900, color: "#2563eb", marginTop: 4 }}>
                            {totalEligible.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeDistributionSummary}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={showMasterListModal} toggle={closeMasterListModal} size="sm" centered>
        <form onSubmit={handleGenerateMasterList}>
          <ModalHeader toggle={closeMasterListModal}>Component Cycles</ModalHeader>
          <ModalBody>
            {masterListCyclesLoading ? (
              <div className="text-center py-3 text-muted">Loading cycles...</div>
            ) : (
              <>
                <div className="mb-3">
                  <label className="form-label mb-1">Select Distribution cycle</label>
                  <Input
                    type="select"
                    value={masterListForm.cycle_id}
                    onChange={(e) => handleMasterListFormChange("cycle_id", e.target.value)}
                    required
                  >
                    <option value="">Select Cycle</option>
                    {masterListCycles.map((c) => (
                      <option key={c.cycle_id} value={c.cycle_id}>
                        {c.cycle_name}
                      </option>
                    ))}
                  </Input>
                </div>
                <div className="mb-3">
                  <label className="form-label mb-1">MasterList Type</label>
                  <Input
                    type="select"
                    value={masterListForm.masterlist_type}
                    onChange={(e) => handleMasterListFormChange("masterlist_type", e.target.value)}
                    required
                  >
                    <option value="">Select Type</option>
                    <option value={10000}>Registration MasterList</option>
                    <option value={10001}>Attendance MasterList</option>
                  </Input>
                </div>
                <div className="mb-3">
                  <span className="form-label d-block mb-1">Template Type</span>
                  <div className="d-flex align-items-center gap-3">
                    <label className="d-flex align-items-center gap-1 mb-0">
                      <Input
                        type="radio"
                        name="template_status"
                        value="old"
                        checked={masterListForm.template_status === "old"}
                        onChange={(e) => handleMasterListFormChange("template_status", e.target.value)}
                      />
                      OLD
                    </label>
                    <label className="d-flex align-items-center gap-1 mb-0">
                      <Input
                        type="radio"
                        name="template_status"
                        value="new"
                        checked={masterListForm.template_status === "new"}
                        onChange={(e) => handleMasterListFormChange("template_status", e.target.value)}
                      />
                      NEW
                    </label>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label mb-1">Registration Start Date</label>
                  <Input
                    type="date"
                    value={masterListForm.start_date}
                    onChange={(e) => handleMasterListFormChange("start_date", e.target.value)}
                  />
                </div>
                <div className="mb-0">
                  <label className="form-label mb-1">Registration end Date</label>
                  <Input
                    type="date"
                    value={masterListForm.end_date}
                    onChange={(e) => handleMasterListFormChange("end_date", e.target.value)}
                  />
                </div>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="link" className="me-auto text-secondary" onClick={closeMasterListModal} disabled={isGeneratingMasterList}>
              Cancel
            </Button>
            <Button color="info" type="submit" disabled={isGeneratingMasterList || masterListCyclesLoading}>
              {isGeneratingMasterList ? "Processing..." : "Get Master List"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Move component to another project modal */}
      <Modal isOpen={showMoveComponentModal} toggle={() => setShowMoveComponentModal(false)}>
        <ModalHeader toggle={() => setShowMoveComponentModal(false)}>
          Move Component to Another Project
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <strong>Component:</strong> {activityName}
          </div>
          <div className="mb-3">
            <strong>Current Project:</strong> {projectName || projectId || "N/A"}
          </div>
          <div className="mb-3">
            <label className="form-label mb-1" style={{ fontWeight: 600 }}>Select New Project</label>
            {allProjectsLoading ? (
              <div className="text-muted">Loading projects...</div>
            ) : (
              <Input
                type="select"
                value={selectedNewProjectId}
                onChange={(e) => setSelectedNewProjectId(e.target.value)}
              >
                <option value="">-- Select a project --</option>
                {allProjects
                  .filter((p) => p.id !== projectId)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.project_name || p.name || p.id}
                    </option>
                  ))}
              </Input>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="link" className="me-auto text-secondary" onClick={() => setShowMoveComponentModal(false)} disabled={isMovingComponent}>
            Cancel
          </Button>
          <Button
            color="warning"
            disabled={!selectedNewProjectId || isMovingComponent}
            onClick={async () => {
              setIsMovingComponent(true);
              try {
                const res = await updateComponentProjectApi({
                  project_id: projectId,
                  component_id,
                  new_project_id: selectedNewProjectId,
                });
                const d = res?.data;
                if (d?.status === 1 || d?.status === 10001) {
                  cogoToast.success(d?.message || "Component moved successfully.");
                  setShowMoveComponentModal(false);
                } else {
                  cogoToast.error(d?.message || "Failed to move component.");
                }
              } catch {
                cogoToast.error("An error occurred while moving the component.");
              } finally {
                setIsMovingComponent(false);
              }
            }}
          >
            {isMovingComponent ? "Moving..." : "Update"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ActivityBeneficiaries;
